/**
 * Contact page
 *
 * The product currently has only one support channel — an email
 * address — so this page is a thin wrapper that surfaces it as a
 * proper /contact URL (better than a bare mailto: scattered across
 * footers). When we add a contact form or help docs, they go here.
 */
import { Link } from "react-router-dom";
import { Mail, MessageCircle } from "lucide-react";
import Logo from "../../components/ui/Logo";

const SUPPORT_EMAIL = "hello@teacherdashboard.ai";

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-white text-stone-800">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-3 focus:py-2 focus:bg-brand-700 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <header className="border-b border-stone-100">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="w-8 h-8" aria-hidden="true" />
            <span className="font-bold text-stone-900">TeacherDashboard</span>
          </Link>
          <Link to="/" className="text-sm text-stone-600 hover:text-stone-900">
            Back to home
          </Link>
        </div>
      </header>

      <main id="main" tabIndex={-1} className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-center space-y-3 mb-10">
          <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-brand-50 text-brand-700">
            <MessageCircle className="w-7 h-7" aria-hidden="true" />
          </div>
          <h1 className="text-3xl font-bold text-stone-900">Get in touch</h1>
          <p className="text-stone-600 max-w-md mx-auto">
            Bug reports, feature requests, billing questions, partnerships. We read every email.
          </p>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-stone-50 p-8 text-center">
          <Mail className="w-6 h-6 text-stone-500 mx-auto mb-3" />
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-2xl font-semibold text-brand-700 hover:text-brand-800 break-all"
          >
            {SUPPORT_EMAIL}
          </a>
          <p className="text-sm text-stone-500 mt-3">
            We typically respond within one business day.
          </p>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 gap-4 text-sm">
          <Link
            to="/privacy"
            className="rounded-xl border border-stone-200 p-4 hover:bg-stone-50 transition-colors"
          >
            <div className="font-semibold text-stone-900 mb-1">Privacy Policy</div>
            <div className="text-stone-600">How we handle your data and your students' first names.</div>
          </Link>
          <Link
            to="/terms"
            className="rounded-xl border border-stone-200 p-4 hover:bg-stone-50 transition-colors"
          >
            <div className="font-semibold text-stone-900 mb-1">Terms of Service</div>
            <div className="text-stone-600">The agreement between us when you use TeacherDashboard.</div>
          </Link>
        </div>
      </main>
    </div>
  );
}
